/**
 * Pure formatting helpers shared by the /admin portal tabs and drawers.
 */

/** "1 tenant", "4 people": a count with its noun in the right number. */
export function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "8d1e5b00…0001": the first 8 and last 4 characters of a long id. */
export function shortenId(id: string): string {
  if (id.length <= 12) return id
  return `${id.slice(0, 8)}…${id.slice(-4)}`
}

/**
 * "19 May 2026", or null when the value is absent, unparseable or the .NET
 * DateTime.MinValue sentinel ("0001-01-01T00:00:00") a never-scanned tenant
 * carries.
 */
export function formatLongDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 2000) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** "14:02" in the viewer's clock, for a provenance line. */
export function formatClock(date: Date): string {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

/** "1h 4m", "12m 30s", "45s" from milliseconds (queue durations and waits). */
export function formatDuration(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) return `${hours}h ${minutes % 60}m`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

/**
 * Milliseconds from a .NET TimeSpan as System.Text.Json writes it ("00:05:00",
 * "1.02:03:04.5000000"), or null when the value does not parse. A finite,
 * non-negative number is read as milliseconds (the unit the other queue fields use).
 */
export function timeSpanToMs(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null
  if (typeof value !== 'string') return null
  const m = /^(?:(\d+)\.)?(\d+):(\d+):(\d+)(?:\.(\d+))?$/.exec(value)
  if (!m) return null
  const [, days, hours, minutes, seconds, fraction] = m
  const wholeSeconds = ((Number(days ?? 0) * 24 + Number(hours)) * 60 + Number(minutes)) * 60 + Number(seconds)
  return wholeSeconds * 1000 + (fraction ? Number(`0.${fraction}`) * 1000 : 0)
}

const JOB_TYPE_LABEL: Record<string, string> = {
  ConfigurationScan: 'Configuration scan',
  BaselineRefresh: 'Baseline refresh',
  DriftDetection: 'Drift detection',
  ScubaScan: 'SCuBA scan',
  UserInsightsRefresh: 'User analytics refresh',
}

/** A scan job's type in words ("Configuration scan"); an unknown type reads as sent. */
export function jobTypeLabel(jobType: string): string {
  return JOB_TYPE_LABEL[jobType] ?? jobType
}
